/**
 * OmegaCeoBriefingDetailPage — Phase 36.7a.
 *
 * Full view of a single CEO daily briefing.
 * Narrative rendered verbatim (F-1/Q-5 compliance).
 */

import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { statusBadgeColor, fmtBriefingDateShort } from './OmegaCeoBriefingsPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface BrainSnapshot {
  market_intel_top_tam_category: string;
  market_intel_top_tam_usd_cents: number;
  market_intel_competitor_alerts: number;
  market_intel_regulatory_urgent: number;
  market_intel_pricing_median_usd_cents: number;
}

interface BriefingDetailResponse {
  briefing_id: string;
  generated_at: number;
  narrative: string;
  context_snapshot: string;
  status: string;
  narrative_length: number;
  brain_snapshot?: BrainSnapshot;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchBriefingById(id: string): Promise<BriefingDetailResponse> {
  return apiFetch<BriefingDetailResponse>(`/internal/ceo/briefings/${encodeURIComponent(id)}`);
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaCeoBriefingDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { briefing_id } = useParams<{ briefing_id: string }>();

  const { data, isPending, isError } = useQuery({
    queryKey: ['ceo', 'briefing', briefing_id],
    queryFn: () => fetchBriefingById(briefing_id!),
    enabled: !!briefing_id,
    refetchInterval: 0,
  });

  const dateLabel = data ? fmtBriefingDateShort(data.generated_at) : briefing_id ?? '';

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'CEO', to: '/omega/ceo' },
        { label: 'Briefings', to: '/omega/ceo/briefings' },
        { label: dateLabel },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, direction: 'rtl' }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em' }}>
          {data ? fmtBriefingDateShort(data.generated_at) : '—'}
        </h1>
        {data && (
          <span style={{
            fontSize: '7.5pt', fontWeight: 700, padding: '1px 6px',
            border: `1px solid ${statusBadgeColor(data.status)}`,
            color: statusBadgeColor(data.status),
            borderRadius: 3, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            {data.status}
          </span>
        )}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load briefing.</div>}

      {data && (
        <>
          {/* Narrative section — verbatim (Q-5) */}
          <section style={{ marginBottom: 24 }}>
            <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
              Briefing
            </div>
            <div style={{
              background: 'var(--omega-surface, rgba(255,255,255,0.04))',
              border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
              borderRadius: 6, padding: '16px 18px',
            }}>
              <pre style={{
                margin: 0, fontFamily: 'var(--omega-mono)', fontSize: '9pt',
                color: 'var(--omega-ink)', whiteSpace: 'pre-wrap', direction: 'rtl',
              }}>
                {data.narrative}
              </pre>
            </div>
          </section>

          {/* Market Intelligence section — Phase 40.9 */}
          {data.brain_snapshot && (
            <section style={{ marginBottom: 24 }}>
              <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
                Market Intelligence
              </div>
              <div style={{
                background: 'var(--omega-surface, rgba(255,255,255,0.04))',
                border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                borderRadius: 6, padding: '16px 18px',
              }}>
                {data.brain_snapshot.market_intel_top_tam_usd_cents > 0 ? (
                  <ul style={{ margin: 0, padding: '0 0 0 18px', fontFamily: 'var(--omega-mono)', fontSize: '9pt', color: 'var(--omega-ink)' }}>
                    <li>
                      Top opportunity: {data.brain_snapshot.market_intel_top_tam_category}
                      {' — '}TAM: ${(data.brain_snapshot.market_intel_top_tam_usd_cents / 100).toLocaleString()}
                    </li>
                    <li>Competitor alerts: {data.brain_snapshot.market_intel_competitor_alerts}</li>
                    <li>Urgent regulatory events: {data.brain_snapshot.market_intel_regulatory_urgent}</li>
                    <li>
                      Market pricing median: ${(data.brain_snapshot.market_intel_pricing_median_usd_cents / 100).toLocaleString()}/month
                    </li>
                  </ul>
                ) : (
                  <p style={{ margin: 0, fontFamily: 'var(--omega-mono)', fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>
                    No market intelligence data available for this briefing.
                  </p>
                )}
              </div>
            </section>
          )}
        </>
      )}

      <Link
        to="/omega/ceo/briefings"
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
          fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
          textDecoration: 'none', letterSpacing: '0.04em',
        }}
      >
        ← Back to briefings
      </Link>
    </div>
  );
}
